import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import {
  computeGranularity,
  toMetricGranularity,
  type DateRangeInput,
  type IngestionFilter,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
  IngestionBreakdown,
} from '@/lib/api/reporting/metrics';

interface IngestionStatCardsProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters?: IngestionFilter;
}

interface IngestionOutcomesChartProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters?: IngestionFilter;
}

const OUTCOME_COLORS: Record<string, string> = {
  Processed: '#10b981',
  Filtered: '#f59e0b',
  Failed: '#ef4444',
  Skipped: '#6b7280',
};

// --- Stat Cards (standalone) ---

export function IngestionStatCards({ tenantId, dateRange, filters }: IngestionStatCardsProps) {
  const dateStart = new Date(dateRange.dateStart);
  const dateEnd = new Date(dateRange.dateEnd);

  const { data: totalsData, isLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: IngestionBreakdown.Outcome,
      dateStart,
      dateEnd,
      siteId: filters?.projectId,
      cameraId: filters?.videoProcessorConfigId,
    })
  );

  const totals = useMemo(() => {
    const result = { total: 0, processed: 0, filtered: 0, failed: 0 };
    if (!totalsData?.data) return result;
    for (const point of totalsData.data) {
      const outcome = point.attributes?.['outcome'] ?? '';
      const value = point.value ?? 0;
      result.total += value;
      if (outcome === 'Processed') result.processed += value;
      else if (outcome === 'Filtered') result.filtered += value;
      else if (outcome === 'Failed') result.failed += value;
    }
    return result;
  }, [totalsData]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-4 gap-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-[60px] rounded-lg" />
        ))}
      </div>
    );
  }

  const pct = (n: number, d: number) => (d === 0 ? '0%' : `${((n / d) * 100).toFixed(1)}%`);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <StatCard label="Total Logs" value={totals.total.toLocaleString()} />
      <StatCard label="Processed" value={pct(totals.processed, totals.total)} color="#10b981" />
      <StatCard label="Filtered" value={pct(totals.filtered, totals.total)} color="#f59e0b" />
      <StatCard label="Failed" value={pct(totals.failed, totals.total)} color="#ef4444" />
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-stretch overflow-hidden rounded-lg border border-[#2E3835] bg-[#1a1a1a] transition-colors hover:border-white/10">
      {color && <div className="w-1 shrink-0" style={{ backgroundColor: color }} />}
      <div className="px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-white/35">{label}</div>
        <div
          className="mt-0.5 text-xl font-bold tabular-nums"
          style={color ? { color } : { color: 'white' }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// --- Outcomes Over Time Chart (standalone) ---

export function IngestionOutcomesChart({ tenantId, dateRange, filters }: IngestionOutcomesChartProps) {
  const granularity = computeGranularity(dateRange);
  const dateStart = new Date(dateRange.dateStart);
  const dateEnd = new Date(dateRange.dateEnd);

  const { data: timelineData, isLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: IngestionBreakdown.Outcome,
      granularity: toMetricGranularity(granularity),
      dateStart,
      dateEnd,
      siteId: filters?.projectId,
      cameraId: filters?.videoProcessorConfigId,
    })
  );

  const tickFormat =
    granularity === 'HOUR'
      ? {
          month: 'short' as const,
          day: 'numeric' as const,
          hour: '2-digit' as const,
          minute: '2-digit' as const,
        }
      : { month: 'short' as const, day: 'numeric' as const };

  const { chartData, outcomes } = useMemo(() => {
    if (!timelineData?.data?.length) return { chartData: [], outcomes: [] };

    const outcomeSet = new Set<string>();
    const byBucket: Record<string, Record<string, number>> = {};

    for (const point of timelineData.data) {
      const outcome = point.attributes?.['outcome'] ?? '';
      const bucket = point.key ?? '';
      outcomeSet.add(outcome);
      if (!byBucket[bucket]) byBucket[bucket] = {};
      byBucket[bucket][outcome] = (byBucket[bucket][outcome] ?? 0) + (point.value ?? 0);
    }

    return {
      chartData: Object.entries(byBucket)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([bucket, counts]) => ({
          hour: new Date(bucket).toLocaleString(undefined, tickFormat),
          ...counts,
        })),
      outcomes: [...outcomeSet].sort(),
    };
  }, [timelineData, tickFormat]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a] p-3">
        <Skeleton className="h-[240px] w-full" />
      </div>
    );
  }

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a]">
      <div className="px-3 pt-3 pb-1">
        <h3 className="text-sm font-medium text-white">Outcomes Over Time</h3>
        <p className="text-[10px] text-white/40">
          {granularity === 'HOUR' ? 'Hourly' : granularity === 'DAY' ? 'Daily' : 'Weekly'} volume
          by outcome
        </p>
      </div>
      <div className="px-3 pb-3">
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2E3835" vertical={false} />
              <XAxis
                dataKey="hour"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }}
                interval="preserveStartEnd"
                axisLine={{ stroke: '#2E3835' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, label, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="rounded-lg border border-[#2E3835] bg-black/95 px-3 py-2 text-xs backdrop-blur-sm">
                      <div className="mb-1 font-medium text-white/70">{label}</div>
                      {payload.map((p) => {
                        const val = (p.value as number) ?? 0;
                        if (val === 0) return null;
                        return (
                          <div
                            key={p.dataKey as string}
                            className="flex items-center gap-1.5 py-0.5"
                          >
                            <span
                              className="inline-block h-2 w-2 shrink-0 rounded-sm"
                              style={{ backgroundColor: p.color }}
                            />
                            <span className="text-white/70">{p.dataKey as string}</span>
                            <span className="ml-auto pl-3 tabular-nums text-white/50">
                              {val.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
                cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              {outcomes.map((outcome) => (
                <Line
                  key={outcome}
                  type="monotone"
                  dataKey={outcome}
                  stroke={OUTCOME_COLORS[outcome] ?? '#6b7280'}
                  strokeWidth={2}
                  dot={false}
                  name={outcome}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// Keep the original component as a re-export for backward compatibility during migration
export function IngestionStageSummary({
  tenantId,
  dateRange,
  filters,
}: {
  tenantId: string;
  dateRange: DateRangeInput;
  filters?: IngestionFilter;
}) {
  return (
    <div className="space-y-3">
      <IngestionStatCards tenantId={tenantId} dateRange={dateRange} filters={filters} />
      <IngestionOutcomesChart tenantId={tenantId} dateRange={dateRange} filters={filters} />
    </div>
  );
}
